import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
} from "react";
import {
  applyTouchNavStep,
  applyWheelGesture,
  clientToStage,
  DEFAULT_VIEWPORT,
  fitWorldBounds,
  GRID_BASE_STEP_PX,
  paintBoardGridCanvas,
  resolveWheelGesture,
  SCALE_MAX,
  SCALE_MIN,
  viewportCenterOnOrigin,
  viewportZoomAtPoint,
  viewportZoomByFactor,
  ZOOM_BUTTON_FACTOR,
  type BoardViewportState,
  type WorldBounds,
} from "../lib/board/boardViewport";
import { getBoardGridStyle, subscribeBoardGridStyle } from "../lib/board/boardGridPrefs";
import {
  applyWheelToScrollable,
  resolveWheelScrollTarget,
  type WheelScrollLatch,
} from "../editor/document/scrollWheelTarget";
import { installBoardAudioUnlock } from "../lib/audio/boardAudioUnlock";

type PanStart = {
  x: number;
  y: number;
  panX: number;
  panY: number;
  lastX: number;
  lastY: number;
  lastT: number;
};

const REACT_COMMIT_MS = 48;
const NAV_IDLE_MS = 140;
/** Тачпад часто шлёт wheel пачками с паузами >200 мс — не сбрасываем session слишком рано. */
const WHEEL_IDLE_MIN_MS = 420;
const WHEEL_IDLE_MAX_MS = 700;
/** Мин. скорость инерции (px/мс). */
const INERTIA_MIN_SPEED = 0.08;
const INERTIA_MAX_SPEED = 2.6;
const INERTIA_DECAY_PER_16MS = 0.955;
/** Если последнее движение было слишком давно — без инерции. */
const INERTIA_MAX_AGE_MS = 90;

function noteUserCamera() {
  window.dispatchEvent(new CustomEvent("wb:user-camera"));
}

function viewportTransform(vp: BoardViewportState): string {
  // translate3d — отдельный compositor-слой, меньше CPU-paint на больших экранах.
  return `translate3d(${vp.panX}px, ${vp.panY}px, 0) scale(${vp.scale})`;
}

type DomCache = {
  inner: HTMLElement | null;
  gridCanvas: HTMLCanvasElement | null;
  cssW: number;
  cssH: number;
};

function getDomCache(viewportEl: HTMLElement): DomCache {
  const anyEl = viewportEl as HTMLElement & { __wbVpCache?: DomCache };
  if (!anyEl.__wbVpCache) {
    anyEl.__wbVpCache = { inner: null, gridCanvas: null, cssW: 0, cssH: 0 };
  }
  return anyEl.__wbVpCache;
}

function writeCameraTransform(viewportEl: HTMLElement | null, vp: BoardViewportState) {
  if (!viewportEl) return;
  const cache = getDomCache(viewportEl);
  if (!cache.inner || !cache.inner.isConnected) {
    const found = viewportEl.querySelector(".wb-board-viewport__inner");
    cache.inner = found instanceof HTMLElement ? found : null;
  }
  if (cache.inner) {
    cache.inner.style.transform = viewportTransform(vp);
  }
}

/** Сброс compositor-кэша: после zoom/pan Chromium снова рисует SVG/HTML, а не тянет bitmap. */
function forceSharpViewportLayer(viewportEl: HTMLElement | null, vp: BoardViewportState) {
  if (!viewportEl) return;
  const inner = viewportEl.querySelector(".wb-board-viewport__inner");
  if (!(inner instanceof HTMLElement)) return;
  inner.classList.remove("is-compositor-nav");
  inner.style.willChange = "";
  writeCameraTransform(viewportEl, vp);
  void inner.getBoundingClientRect();
  requestAnimationFrame(() => {
    writeCameraTransform(viewportEl, vp);
  });
}

function paintViewportGrid(
  viewportEl: HTMLElement | null,
  vp: BoardViewportState,
  opts?: { light?: boolean; forceSize?: boolean }
) {
  if (!viewportEl) return;
  const cache = getDomCache(viewportEl);
  if (!cache.gridCanvas || !cache.gridCanvas.isConnected) {
    const found = viewportEl.querySelector(".wb-board-grid-canvas");
    cache.gridCanvas = found instanceof HTMLCanvasElement ? found : null;
  }
  const canvas = cache.gridCanvas;
  if (!canvas) return;

  let cssW = cache.cssW;
  let cssH = cache.cssH;
  if (opts?.forceSize || cssW < 8 || cssH < 8) {
    const rect = viewportEl.getBoundingClientRect();
    cssW = rect.width;
    cssH = rect.height;
    cache.cssW = cssW;
    cache.cssH = cssH;
  }
  paintBoardGridCanvas(canvas, vp, cssW, cssH, {
    light: opts?.light,
    style: getBoardGridStyle(),
  });
}

export function useBoardViewport() {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState<BoardViewportState>(DEFAULT_VIEWPORT);
  const viewportRefState = useRef(viewport);
  viewportRefState.current = viewport;

  /** Актуальный viewport для hit-test / DOM во время жеста (без ожидания React). */
  const liveViewportRef = useRef<BoardViewportState>(DEFAULT_VIEWPORT);

  const panRef = useRef<PanStart | null>(null);
  /** Рука / Space / СКМ / ПКМ / инерция: React не коммитим до конца жеста. */
  const panSessionRef = useRef(false);
  /** Двухпальцевый pan/pinch тачпада (wheel) — без React до конца OS-инерции. */
  const wheelSessionRef = useRef(false);
  /** Внутренний список, который крутили последним, и время последнего тика. */
  const wheelScrollLatchRef = useRef<WheelScrollLatch>({ el: null, ts: 0 });
  const spaceRef = useRef(false);
  const navIdleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navTeardownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reactCommitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rectCacheRef = useRef<{ rect: DOMRect; until: number } | null>(null);
  /** EMA-скорость для инерции после отпускания мыши. */
  const panVelocityRef = useRef({ vx: 0, vy: 0, t: 0 });
  const inertiaRafRef = useRef(0);
  const inertiaActiveRef = useRef(false);
  const domRafRef = useRef(0);
  const navigatingRef = useRef(false);
  /** will-change только для чистого pan (translate). Zoom с bitmap-кэшем = «мыло». */
  const compositorNavRef = useRef(false);

  const commitReactViewport = useCallback((vp: BoardViewportState) => {
    liveViewportRef.current = vp;
    viewportRefState.current = vp;
    setViewport(vp);
  }, []);

  const clearPanVelocity = useCallback(() => {
    panVelocityRef.current = { vx: 0, vy: 0, t: 0 };
  }, []);

  const recordPanVelocity = useCallback((dx: number, dy: number, t = performance.now()) => {
    if (inertiaActiveRef.current) return;
    if (!dx && !dy) return;
    const prev = panVelocityRef.current;
    const rawDt = prev.t ? t - prev.t : 16;
    const dt = Math.max(4, Math.min(28, rawDt));
    const a = 0.45;
    panVelocityRef.current = {
      vx: a * (dx / dt) + (1 - a) * prev.vx,
      vy: a * (dy / dt) + (1 - a) * prev.vy,
      t,
    };
  }, []);

  const stopInertia = useCallback(() => {
    if (inertiaRafRef.current) {
      cancelAnimationFrame(inertiaRafRef.current);
      inertiaRafRef.current = 0;
    }
    inertiaActiveRef.current = false;
  }, []);

  /** Presence при своём pan/zoom (мышь + тачпад). navigatingRef — запас на редкие wheel-тики. */
  const emitLiveViewportPresence = useCallback(() => {
    if (
      !(
        panSessionRef.current ||
        wheelSessionRef.current ||
        inertiaActiveRef.current ||
        navigatingRef.current
      )
    ) {
      return;
    }
    window.dispatchEvent(new CustomEvent("wb:live-viewport"));
  }, []);

  /** Один rAF на кадр: camera + сетка вместе (и мышь, и тачпад). */
  const scheduleDomWrite = useCallback(() => {
    if (domRafRef.current) return;
    domRafRef.current = requestAnimationFrame(() => {
      domRafRef.current = 0;
      const vpState = liveViewportRef.current;
      writeCameraTransform(viewportRef.current, vpState);
      paintViewportGrid(viewportRef.current, vpState, { light: navigatingRef.current });
      emitLiveViewportPresence();
    });
  }, [emitLiveViewportPresence]);

  const flushDomNow = useCallback(() => {
    if (domRafRef.current) {
      cancelAnimationFrame(domRafRef.current);
      domRafRef.current = 0;
    }
    const vpState = liveViewportRef.current;
    writeCameraTransform(viewportRef.current, vpState);
    paintViewportGrid(viewportRef.current, vpState, { light: false });
    emitLiveViewportPresence();
  }, [emitLiveViewportPresence]);

  const scheduleReactCommit = useCallback(
    (immediate = false) => {
      if (immediate) {
        if (reactCommitTimerRef.current) {
          clearTimeout(reactCommitTimerRef.current);
          reactCommitTimerRef.current = null;
        }
        commitReactViewport(liveViewportRef.current);
        return;
      }
      if (panSessionRef.current || inertiaActiveRef.current || wheelSessionRef.current) return;
      if (reactCommitTimerRef.current) return;
      reactCommitTimerRef.current = setTimeout(() => {
        reactCommitTimerRef.current = null;
        if (panSessionRef.current || inertiaActiveRef.current || wheelSessionRef.current) return;
        commitReactViewport(liveViewportRef.current);
      }, REACT_COMMIT_MS);
    },
    [commitReactViewport]
  );

  const applyLiveViewport = useCallback(
    (next: BoardViewportState, opts?: { syncReact?: boolean }) => {
      liveViewportRef.current = next;
      viewportRefState.current = next;
      if (opts?.syncReact) {
        flushDomNow();
        scheduleReactCommit(true);
      } else {
        scheduleDomWrite();
        scheduleReactCommit(false);
      }
      // Синхронно (не ждать rAF): на тачпаде иначе presence легко «проглатывается»
      // между редкими wheel и idle finishNavigation.
      emitLiveViewportPresence();
    },
    [emitLiveViewportPresence, flushDomNow, scheduleDomWrite, scheduleReactCommit]
  );

  const setViewportNavigating = useCallback(
    (on: boolean, opts?: { compositor?: boolean }) => {
      const compositor = on && (opts?.compositor ?? false);
      const uiOn = on;

      if (navigatingRef.current === uiOn && compositorNavRef.current === compositor) return;

      navigatingRef.current = uiOn;
      compositorNavRef.current = compositor;

      const viewportEl = viewportRef.current;
      const inner = viewportEl?.querySelector(".wb-board-viewport__inner");
      if (viewportEl) viewportEl.classList.toggle("is-navigating", uiOn);
      if (inner instanceof HTMLElement) {
        inner.classList.toggle("is-navigating", uiOn);
        inner.classList.toggle("is-compositor-nav", compositor);
        if (!compositor) inner.style.willChange = "";
      }
      window.dispatchEvent(new CustomEvent("wb:viewport-navigating", { detail: uiOn }));
    },
    []
  );

  const requestSharpViewport = useCallback(() => {
    if (
      navigatingRef.current ||
      panSessionRef.current ||
      wheelSessionRef.current ||
      inertiaActiveRef.current
    ) {
      return;
    }
    forceSharpViewportLayer(viewportRef.current, liveViewportRef.current);
  }, []);

  const finishNavigation = useCallback(() => {
    if (inertiaActiveRef.current || panSessionRef.current || panRef.current) return;
    flushDomNow();
    wheelSessionRef.current = false;
    scheduleReactCommit(true);
    if (navTeardownTimerRef.current) {
      clearTimeout(navTeardownTimerRef.current);
      navTeardownTimerRef.current = null;
    }
    setViewportNavigating(false);
    forceSharpViewportLayer(viewportRef.current, liveViewportRef.current);
  }, [flushDomNow, scheduleReactCommit, setViewportNavigating]);

  const bumpViewportNavigating = useCallback(
    (idleMs = NAV_IDLE_MS, compositor = false) => {
      noteUserCamera();
      setViewportNavigating(true, { compositor });
      if (navTeardownTimerRef.current) {
        clearTimeout(navTeardownTimerRef.current);
        navTeardownTimerRef.current = null;
      }
      if (navIdleTimerRef.current) clearTimeout(navIdleTimerRef.current);
      navIdleTimerRef.current = setTimeout(() => {
        navIdleTimerRef.current = null;
        finishNavigation();
      }, idleMs);
    },
    [finishNavigation, setViewportNavigating]
  );

  const endPanSession = useCallback(() => {
    panSessionRef.current = false;
    finishNavigation();
  }, [finishNavigation]);

  const startInertia = useCallback(() => {
    stopInertia();
    const now = performance.now();
    const vel = panVelocityRef.current;
    clearPanVelocity();

    const age = vel.t ? now - vel.t : Infinity;
    let vx = vel.vx;
    let vy = vel.vy;
    if (!vel.t || age > INERTIA_MAX_AGE_MS) {
      endPanSession();
      return;
    }
    if (age > 16) {
      const k = Math.pow(0.92, (age - 16) / 16);
      vx *= k;
      vy *= k;
    }
    const speed = Math.hypot(vx, vy);
    if (speed < INERTIA_MIN_SPEED) {
      endPanSession();
      return;
    }
    if (speed > INERTIA_MAX_SPEED) {
      const k = INERTIA_MAX_SPEED / speed;
      vx *= k;
      vy *= k;
    }

    panSessionRef.current = true;
    inertiaActiveRef.current = true;
    setViewportNavigating(true, { compositor: true });
    let last = now;

    const tick = (frameNow: number) => {
      if (!inertiaActiveRef.current) return;
      const frameDt = Math.min(34, Math.max(0, frameNow - last));
      last = frameNow;
      const decay = Math.pow(INERTIA_DECAY_PER_16MS, frameDt / 16);
      vx *= decay;
      vy *= decay;
      const dx = vx * frameDt;
      const dy = vy * frameDt;
      if (Math.hypot(vx, vy) < 0.02 || Math.hypot(dx, dy) < 0.3) {
        stopInertia();
        endPanSession();
        return;
      }
      const cur = liveViewportRef.current;
      const next = { ...cur, panX: cur.panX + dx, panY: cur.panY + dy };
      liveViewportRef.current = next;
      viewportRefState.current = next;
      writeCameraTransform(viewportRef.current, next);
      paintViewportGrid(viewportRef.current, next, { light: true });
      // Пока доска летит, камеру пишем сами, минуя общий путь, — и о ней
      // нужно сказать всем, кто за ней следует. Без этого поле набора текста
      // и панели замирали на месте, а в конце полёта прыгали следом.
      emitLiveViewportPresence();
      inertiaRafRef.current = requestAnimationFrame(tick);
    };

    inertiaRafRef.current = requestAnimationFrame(tick);
  }, [
    clearPanVelocity,
    emitLiveViewportPresence,
    endPanSession,
    setViewportNavigating,
    stopInertia,
  ]);

  const endPanGesture = useCallback(() => {
    startInertia();
  }, [startInertia]);

  const readViewportRect = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return null;
    const now = performance.now();
    const cached = rectCacheRef.current;
    if (cached && cached.until > now) return cached.rect;
    const rect = el.getBoundingClientRect();
    rectCacheRef.current = { rect, until: now + 16 };
    return rect;
  }, []);

  const mapClientToStageStable = useRef((clientX: number, clientY: number) => {
    const el = viewportRef.current;
    if (!el) return { x: 0, y: 0 };
    return clientToStage(clientX, clientY, el.getBoundingClientRect(), liveViewportRef.current);
  });

  const mapClientToStage = useCallback(
    (clientX: number, clientY: number) => mapClientToStageStable.current(clientX, clientY),
    []
  );

  const centerOnOrigin = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    const next = viewportCenterOnOrigin(liveViewportRef.current, width, height);
    applyLiveViewport(next, { syncReact: true });
  }, [applyLiveViewport]);

  const zoomByFactor = useCallback(
    (factor: number) => {
      const rect = readViewportRect();
      if (!rect) return;
      noteUserCamera();
      stopInertia();
      clearPanVelocity();
      panSessionRef.current = false;
      bumpViewportNavigating();
      const next = viewportZoomByFactor(
        liveViewportRef.current,
        rect,
        rect.left + rect.width / 2,
        rect.top + rect.height / 2,
        factor
      );
      applyLiveViewport(next, { syncReact: true });
    },
    [
      applyLiveViewport,
      bumpViewportNavigating,
      clearPanVelocity,
      readViewportRect,
      stopInertia,
    ]
  );

  /** Совместимость: zoomBy(±0.1) из старых кнопок. */
  const zoomBy = useCallback(
    (delta: number) => {
      const rect = readViewportRect();
      if (!rect) return;
      bumpViewportNavigating();
      const next = viewportZoomAtPoint(
        liveViewportRef.current,
        rect,
        rect.left + rect.width / 2,
        rect.top + rect.height / 2,
        delta
      );
      applyLiveViewport(next, { syncReact: true });
    },
    [applyLiveViewport, bumpViewportNavigating, readViewportRect]
  );

  const zoomIn = useCallback(() => {
    zoomByFactor(ZOOM_BUTTON_FACTOR);
  }, [zoomByFactor]);

  const zoomOut = useCallback(() => {
    zoomByFactor(1 / ZOOM_BUTTON_FACTOR);
  }, [zoomByFactor]);

  const resetView = useCallback(() => {
    const el = viewportRef.current;
    if (!el) {
      applyLiveViewport({ scale: 1, panX: 0, panY: 0 }, { syncReact: true });
      return;
    }
    const { width, height } = el.getBoundingClientRect();
    applyLiveViewport(
      viewportCenterOnOrigin({ scale: 1, panX: 0, panY: 0 }, width, height),
      { syncReact: true }
    );
  }, [applyLiveViewport]);

  const endPan = useCallback(() => {
    // pointerup на элементе и на window оба могут сработать — второй раз
    // startInertia() остановил бы только что запущенный coast.
    if (!panRef.current) return;
    panRef.current = null;
    startInertia();
  }, [startInertia]);

  const panBy = useCallback(
    (dx: number, dy: number) => {
      if (inertiaActiveRef.current) {
        stopInertia();
        clearPanVelocity();
      }
      if (!dx && !dy) return;
      noteUserCamera();
      panSessionRef.current = true;
      // Не bumpViewportNavigating: idle mid-drag гонял finish и давал гонки с pointerup.
      setViewportNavigating(true, { compositor: true });
      if (navIdleTimerRef.current) {
        clearTimeout(navIdleTimerRef.current);
        navIdleTimerRef.current = null;
      }
      if (navTeardownTimerRef.current) {
        clearTimeout(navTeardownTimerRef.current);
        navTeardownTimerRef.current = null;
      }
      recordPanVelocity(dx, dy);
      const cur = liveViewportRef.current;
      applyLiveViewport({
        ...cur,
        panX: cur.panX + dx,
        panY: cur.panY + dy,
      });
    },
    [
      applyLiveViewport,
      clearPanVelocity,
      recordPanVelocity,
      setViewportNavigating,
      stopInertia,
    ]
  );

  /**
   * Transform только imperative — React style его не задаёт.
   * После любого React-рендера возвращаем live camera.
   */
  useLayoutEffect(() => {
    writeCameraTransform(viewportRef.current, liveViewportRef.current);
  });

  useEffect(() => {
    return subscribeBoardGridStyle(() => {
      const el = viewportRef.current;
      if (!el) return;
      paintViewportGrid(el, liveViewportRef.current, { forceSize: true });
      el.dataset.gridStyle = getBoardGridStyle();
    });
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    el.dataset.gridStyle = getBoardGridStyle();
    const ro = new ResizeObserver(() => {
      const cache = getDomCache(el);
      cache.cssW = 0;
      cache.cssH = 0;
      paintViewportGrid(el, liveViewportRef.current, {
        forceSize: true,
        light: navigatingRef.current,
      });
    });
    ro.observe(el);
    paintViewportGrid(el, liveViewportRef.current, { forceSize: true });
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const onWheel = (ev: globalThis.WheelEvent) => {
      // Pinch-zoom на тачпаде — всегда камера доски.
      if (!ev.ctrlKey && !ev.metaKey) {
        const now = performance.now();
        const target = resolveWheelScrollTarget(ev, el, wheelScrollLatchRef.current, now);
        if (target) {
          ev.preventDefault();
          ev.stopPropagation();
          // На краю списка applyWheelToScrollable ничего не сдвинет — и это то,
          // что нужно: доска остаётся на месте до конца жеста.
          applyWheelToScrollable(target, ev);
          wheelScrollLatchRef.current = { el: target, ts: now };
          return;
        }
        wheelScrollLatchRef.current = { el: null, ts: 0 };
      }

      ev.preventDefault();
      stopInertia();
      clearPanVelocity();
      panSessionRef.current = false;
      // Весь trackpad/wheel-жест без React до конца OS-инерции (включая pinch).
      wheelSessionRef.current = true;
      const rect = readViewportRect();
      if (!rect) return;
      const gesture = resolveWheelGesture(ev);
      const mag = Math.hypot(ev.deltaX, ev.deltaY);
      // Мелкие delta = хвост инерции → ждём дольше, иначе finish mid-coast.
      const idleMs = mag < 6 ? WHEEL_IDLE_MAX_MS : WHEEL_IDLE_MIN_MS;
      // compositor-слой на зуме НЕ включаем намеренно: Chrome начинает
      // растягивать кэшированный bitmap, и при приближении сначала видно
      // мыло, которое «проявляется» только после жеста. Резкость на каждом
      // кадре здесь важнее плавности — плавность добираем отсечением
      // объектов за экраном (sceneCulling), а не размытием.
      bumpViewportNavigating(idleMs);
      const next = applyWheelGesture(liveViewportRef.current, rect, gesture);
      applyLiveViewport(next);
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [
    applyLiveViewport,
    bumpViewportNavigating,
    clearPanVelocity,
    readViewportRect,
    stopInertia,
  ]);

  useEffect(() => {
    function onSceneRendered() {
      requestSharpViewport();
    }
    window.addEventListener("wb:scene-rendered", onSceneRendered);
    return () => window.removeEventListener("wb:scene-rendered", onSceneRendered);
  }, [requestSharpViewport]);

  useEffect(() => {
    return () => {
      if (navIdleTimerRef.current) clearTimeout(navIdleTimerRef.current);
      if (navTeardownTimerRef.current) clearTimeout(navTeardownTimerRef.current);
      if (reactCommitTimerRef.current) clearTimeout(reactCommitTimerRef.current);
      if (inertiaRafRef.current) cancelAnimationFrame(inertiaRafRef.current);
      if (domRafRef.current) cancelAnimationFrame(domRafRef.current);
    };
  }, []);

  /** Два пальца на touch: pan + pinch zoom (поверх любого инструмента). */
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const pointers = new Map<number, { x: number; y: number }>();
    let nav: { midX: number; midY: number; dist: number } | null = null;

    function isTouchLike(ev: PointerEvent) {
      return ev.pointerType === "touch";
    }

    function targetInViewport(ev: PointerEvent) {
      return ev.target instanceof Node && el.contains(ev.target);
    }

    function pairMetrics(): { midX: number; midY: number; dist: number } | null {
      if (pointers.size < 2) return null;
      const pts = Array.from(pointers.values());
      const a = pts[0]!;
      const b = pts[1]!;
      return {
        midX: (a.x + b.x) / 2,
        midY: (a.y + b.y) / 2,
        dist: Math.max(1, Math.hypot(b.x - a.x, b.y - a.y)),
      };
    }

    function endNavIfNeeded() {
      if (pointers.size < 2) {
        if (nav) {
          panSessionRef.current = false;
          finishNavigation();
        }
        nav = null;
      }
    }

    function onPointerDown(ev: PointerEvent) {
      if (!isTouchLike(ev)) return;
      if (!targetInViewport(ev)) return;

      pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
      if (pointers.size < 2) return;

      ev.preventDefault();
      ev.stopImmediatePropagation();
      const metrics = pairMetrics();
      if (!metrics) return;
      if (!nav) {
        nav = metrics;
        stopInertia();
        clearPanVelocity();
        panSessionRef.current = true;
        setViewportNavigating(true, { compositor: false });
        window.dispatchEvent(new CustomEvent("wb:viewport-gesture-start"));
      } else {
        nav = metrics;
      }
    }

    function onPointerMove(ev: PointerEvent) {
      if (!pointers.has(ev.pointerId)) return;
      pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
      if (!nav || pointers.size < 2) return;

      ev.preventDefault();
      ev.stopImmediatePropagation();
      const next = pairMetrics();
      if (!next) return;
      const prev = nav;
      nav = next;
      const rect = el.getBoundingClientRect();
      applyLiveViewport(applyTouchNavStep(liveViewportRef.current, rect, prev, next));
    }

    function onPointerUp(ev: PointerEvent) {
      if (!pointers.has(ev.pointerId)) return;
      pointers.delete(ev.pointerId);
      endNavIfNeeded();
    }

    const opts: AddEventListenerOptions = { capture: true, passive: false };
    document.addEventListener("pointerdown", onPointerDown, opts);
    document.addEventListener("pointermove", onPointerMove, opts);
    document.addEventListener("pointerup", onPointerUp, opts);
    document.addEventListener("pointercancel", onPointerUp, opts);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, opts);
      document.removeEventListener("pointermove", onPointerMove, opts);
      document.removeEventListener("pointerup", onPointerUp, opts);
      document.removeEventListener("pointercancel", onPointerUp, opts);
    };
  }, [applyLiveViewport, clearPanVelocity, finishNavigation, setViewportNavigating, stopInertia]);

  const onPointerDown = useCallback(
    (ev: PointerEvent) => {
      // Средняя/правая кнопка или Space+ЛКМ — панорама камеры.
      const panButton = ev.button === 1 || ev.button === 2 || spaceRef.current;
      if (!panButton) return;
      // Не перехватываем ПКМ по панелям инструментов / зуму.
      if (
        ev.target instanceof Element &&
        ev.target.closest(
          ".wb-floating-tools, .wb-zoom-controls, .wb-selection-toolbar, .wb-tool-options-popover, .wb-board-toolbar, .wb-board-frames__popover, .wb-text-overlay-editor, .wb-modal, .wb-prompt-dialog"
        )
      ) {
        return;
      }
      ev.preventDefault();
      ev.stopPropagation();
      noteUserCamera();
      stopInertia();
      clearPanVelocity();
      panSessionRef.current = true;
      if (navIdleTimerRef.current) {
        clearTimeout(navIdleTimerRef.current);
        navIdleTimerRef.current = null;
      }
      if (navTeardownTimerRef.current) {
        clearTimeout(navTeardownTimerRef.current);
        navTeardownTimerRef.current = null;
      }
      const v = liveViewportRef.current;
      const now = performance.now();
      panRef.current = {
        x: ev.clientX,
        y: ev.clientY,
        panX: v.panX,
        panY: v.panY,
        lastX: ev.clientX,
        lastY: ev.clientY,
        lastT: now,
      };
      setViewportNavigating(true, { compositor: true });
      const onWindowUp = () => endPan();
      window.addEventListener("pointerup", onWindowUp, { once: true });
      window.addEventListener("pointercancel", onWindowUp, { once: true });
      const surface = (ev.currentTarget as HTMLElement | null) ?? viewportRef.current;
      surface?.setPointerCapture?.(ev.pointerId);
    },
    [clearPanVelocity, endPan, setViewportNavigating, stopInertia]
  );

  // Capture на viewport: виджеты stopPropagation на target, bubble до viewport не доходит.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const listener = (ev: globalThis.PointerEvent) => {
      onPointerDown(ev as unknown as PointerEvent);
    };
    el.addEventListener("pointerdown", listener, true);
    return () => el.removeEventListener("pointerdown", listener, true);
  }, [onPointerDown]);

  // Жест на доске → разблокировка аудио («слушать вместе» без галочки у каждого).
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    return installBoardAudioUnlock(el);
  }, []);

  const onPointerMove = useCallback(
    (ev: PointerEvent) => {
      const pan = panRef.current;
      if (!pan) return;
      const now = performance.now();
      recordPanVelocity(ev.clientX - pan.lastX, ev.clientY - pan.lastY, now);
      pan.lastX = ev.clientX;
      pan.lastY = ev.clientY;
      pan.lastT = now;
      applyLiveViewport({
        ...liveViewportRef.current,
        panX: pan.panX + (ev.clientX - pan.x),
        panY: pan.panY + (ev.clientY - pan.y),
      });
    },
    [applyLiveViewport, recordPanVelocity]
  );

  const onPointerUp = useCallback(() => {
    endPan();
  }, [endPan]);

  const onKeyDown = useCallback((ev: KeyboardEvent) => {
    if (ev.code === "Space" && !(ev.target as HTMLElement)?.isContentEditable) {
      const tag = (ev.target as HTMLElement)?.tagName;
      if (tag !== "INPUT" && tag !== "TEXTAREA") {
        ev.preventDefault();
        spaceRef.current = true;
      }
    }
  }, []);

  const onKeyUp = useCallback((ev: KeyboardEvent) => {
    if (ev.code === "Space") spaceRef.current = false;
  }, []);

  const onContextMenu = useCallback((ev: MouseEvent) => {
    ev.preventDefault();
  }, []);

  const viewportStyle: CSSProperties = {};

  // Transform только через writeCameraTransform — иначе React перетирает live camera.
  const innerStyle: CSSProperties = {
    transformOrigin: "0 0",
  };

  const setViewportState = useCallback(
    (next: BoardViewportState) => {
      noteUserCamera();
      applyLiveViewport(next, { syncReact: true });
    },
    [applyLiveViewport]
  );

  const applyFollowViewport = useCallback(
    (next: BoardViewportState, opts?: { syncReact?: boolean }) => {
      // Без scheduleDomWrite: не шлём wb:live-viewport и не ждём лишний rAF.
      liveViewportRef.current = next;
      viewportRefState.current = next;
      writeCameraTransform(viewportRef.current, next);
      paintViewportGrid(viewportRef.current, next, { light: true });
      if (opts?.syncReact) scheduleReactCommit(true);
      else scheduleReactCommit(false);
    },
    [scheduleReactCommit]
  );

  const getLiveViewport = useCallback(() => liveViewportRef.current, []);

  const fitToWorldBounds = useCallback(
    (bounds: WorldBounds, paddingPx = 56) => {
      const apply = (attempt: number) => {
        const el = viewportRef.current;
        if (!el) {
          if (attempt < 16) requestAnimationFrame(() => apply(attempt + 1));
          return;
        }
        const { width, height } = el.getBoundingClientRect();
        if (width < 8 || height < 8) {
          if (attempt < 16) requestAnimationFrame(() => apply(attempt + 1));
          return;
        }
        applyLiveViewport(fitWorldBounds(width, height, bounds, paddingPx), {
          syncReact: true,
        });
      };
      requestAnimationFrame(() => apply(0));
    },
    [applyLiveViewport]
  );

  return {
    viewportRef,
    viewport,
    viewportStyle,
    innerStyle,
    gridWorldStep: GRID_BASE_STEP_PX,
    mapClientToStage,
    mapClientToStageStable: mapClientToStageStable.current,
    zoomBy,
    zoomByFactor,
    zoomIn,
    zoomOut,
    canZoomIn: viewport.scale < SCALE_MAX - 1e-6,
    canZoomOut: viewport.scale > SCALE_MIN + 1e-6,
    resetView,
    centerOnOrigin,
    panBy,
    endPanGesture,
    setViewportState,
    applyFollowViewport,
    getLiveViewport,
    fitToWorldBounds,
    handlers: {
      onPointerMove,
      onPointerUp,
      onKeyDown,
      onKeyUp,
      onContextMenu,
    },
  };
}
